import { useEffect, useRef, useState } from "react";
import {
  Edit2,
  Headphones,
  Image as ImageIcon,
  Loader2,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MultiSelect } from "@/components/ui/multi-select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { uploadThumbnailToCOS, uploadToCOS } from "@/lib/cos-upload";
import {
  getAllKnownCategoryOptions,
  normalizeCategoryList,
} from "@/lib/category-config";
import {
  addCustomCategoryOption,
  useCategoryOptions,
} from "@/hooks/useCategoryOptions";

interface AudioEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audio: {
    id: string;
    title: string;
    description: string | null;
    audio_url: string;
    thumbnail_url: string | null;
    jlpt_level: "N5" | "N4" | "N3" | "N2" | "N1";
    category: string;
    is_premium: boolean;
    is_published: boolean;
    duration: number;
  };
  onSuccess: () => void;
}

interface SubtitleRow {
  id: string;
  start_time: number;
  end_time: number;
  japanese: string;
  chinese: string;
  romaji: string | null;
  sort_order: number | null;
  isEditing?: boolean;
  isNew?: boolean;
}

const JLPT_LEVELS = ["N5", "N4", "N3", "N2", "N1"] as const;
type JlptLevel = (typeof JLPT_LEVELS)[number];

function formatSecondsToClock(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);
  return `${mins}:${secs.padStart(4, "0")}`;
}

function parseClockToSeconds(value: string): number {
  const parts = value.split(":");
  if (parts.length !== 2) return Number(value) || 0;
  return Number(parts[0] || 0) * 60 + Number(parts[1] || 0);
}

export default function AudioEditDialog({
  open,
  onOpenChange,
  audio,
  onSuccess,
}: AudioEditDialogProps) {
  const { toast } = useToast();

  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<"basic" | "media" | "subtitles">("basic");
  const [saving, setSaving] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const [formData, setFormData] = useState({
    title: audio.title,
    description: audio.description || "",
    jlpt_level: audio.jlpt_level,
    category: audio.category,
    categories: [audio.category],
    is_published: audio.is_published,
    is_premium: audio.is_premium,
    thumbnail_url: audio.thumbnail_url || "",
    audio_url: audio.audio_url,
  });

  const [subtitles, setSubtitles] = useState<SubtitleRow[]>([]);
  const [loadingSubtitles, setLoadingSubtitles] = useState(false);
  const [editingSubtitle, setEditingSubtitle] = useState<SubtitleRow | null>(null);

  const { data: categoryOptions = getAllKnownCategoryOptions(), refetch: refetchCategoryOptions } =
    useCategoryOptions();

  useEffect(() => {
    setFormData({
      title: audio.title,
      description: audio.description || "",
      jlpt_level: audio.jlpt_level,
      category: audio.category,
      categories: [audio.category],
      is_published: audio.is_published,
      is_premium: audio.is_premium,
      thumbnail_url: audio.thumbnail_url || "",
      audio_url: audio.audio_url,
    });
    setActiveTab("basic");
    setSubtitles([]);
    setEditingSubtitle(null);
  }, [audio]);

  useEffect(() => {
    if (activeTab !== "subtitles" || subtitles.length > 0) return;

    const loadSubtitles = async () => {
      setLoadingSubtitles(true);
      try {
        const { data, error } = await supabase
          .from("audio_subtitles")
          .select("*")
          .eq("audio_id", audio.id)
          .order("sort_order", { ascending: true });

        if (error) throw error;
        setSubtitles((data || []) as SubtitleRow[]);
      } catch (error) {
        console.error("Failed to load audio subtitles", error);
        toast({ title: "Failed to load subtitles", variant: "destructive" });
      } finally {
        setLoadingSubtitles(false);
      }
    };

    loadSubtitles();
  }, [activeTab, subtitles.length, audio.id, toast]);

  const createCategory = (input: string) => {
    const created = addCustomCategoryOption(input);
    if (!created) return null;

    void refetchCategoryOptions();
    toast({
      title: "Category created",
      description: `${created.label} is now selectable`,
    });

    return created;
  };

  const saveBasic = async () => {
    if (!formData.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }

    const categories = normalizeCategoryList(formData.categories);
    const category = categories[0] || "story";

    setSaving(true);
    try {
      const { error } = await supabase
        .from("audios")
        .update({
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          jlpt_level: formData.jlpt_level,
          category,
          is_published: formData.is_published,
          is_premium: formData.is_premium,
          thumbnail_url: formData.thumbnail_url || null,
          audio_url: formData.audio_url,
        })
        .eq("id", audio.id);

      if (error) throw error;

      toast({ title: "Saved" });
      onSuccess();
    } catch (error) {
      console.error("Failed to save audio", error);
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadThumbnail = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image", variant: "destructive" });
      return;
    }

    setUploadingThumbnail(true);
    try {
      const result = await uploadThumbnailToCOS(file);
      if (!result.success || !result.url) {
        throw new Error(result.error || "Upload failed");
      }

      setFormData((prev) => ({ ...prev, thumbnail_url: result.url! }));
      toast({ title: "Thumbnail updated" });
    } catch (error) {
      console.error("Thumbnail upload failed", error);
      toast({ title: "Thumbnail upload failed", variant: "destructive" });
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleUploadAudio = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      toast({ title: "Please select an audio file", variant: "destructive" });
      return;
    }

    setUploadingAudio(true);
    try {
      const result = await uploadToCOS({ file, folder: "audios" });
      if (!result.success || !result.url) {
        throw new Error(result.error || "Upload failed");
      }

      setFormData((prev) => ({ ...prev, audio_url: result.url! }));
      toast({ title: "Audio file updated" });
    } catch (error) {
      console.error("Audio upload failed", error);
      toast({ title: "Audio upload failed", variant: "destructive" });
    } finally {
      setUploadingAudio(false);
    }
  };

  const addSubtitle = () => {
    const last = subtitles[subtitles.length - 1];
    const start = last?.end_time || 0;

    const row: SubtitleRow = {
      id: `tmp-${Date.now()}`,
      start_time: start,
      end_time: start + 3,
      japanese: "",
      chinese: "",
      romaji: null,
      sort_order: subtitles.length,
      isEditing: true,
      isNew: true,
    };

    setSubtitles((prev) => [...prev, row]);
    setEditingSubtitle(row);
  };

  const removeSubtitle = async (id: string) => {
    try {
      const { error } = await supabase.from("audio_subtitles").delete().eq("id", id);
      if (error) throw error;

      setSubtitles((prev) => prev.filter((row) => row.id !== id));
      toast({ title: "Subtitle removed" });
    } catch (error) {
      console.error("Failed to remove subtitle", error);
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const saveSubtitle = async (subtitle: SubtitleRow) => {
    try {
      if (subtitle.isNew) {
        const { data, error } = await supabase
          .from("audio_subtitles")
          .insert({
            audio_id: audio.id,
            start_time: subtitle.start_time,
            end_time: subtitle.end_time,
            japanese: subtitle.japanese,
            chinese: subtitle.chinese,
            romaji: subtitle.romaji,
            sort_order: subtitle.sort_order ?? subtitles.length,
          })
          .select()
          .single();

        if (error || !data) throw error || new Error("Insert failed");

        setSubtitles((prev) =>
          prev.map((row) =>
            row.id === subtitle.id ? ({ ...(data as SubtitleRow), isEditing: false } as SubtitleRow) : row,
          ),
        );
      } else {
        const { error } = await supabase
          .from("audio_subtitles")
          .update({
            start_time: subtitle.start_time,
            end_time: subtitle.end_time,
            japanese: subtitle.japanese,
            chinese: subtitle.chinese,
            romaji: subtitle.romaji,
          })
          .eq("id", subtitle.id);

        if (error) throw error;

        setSubtitles((prev) =>
          prev.map((row) =>
            row.id === subtitle.id ? ({ ...subtitle, isEditing: false } as SubtitleRow) : row,
          ),
        );
      }

      setEditingSubtitle(null);
      toast({ title: "Subtitle saved" });
    } catch (error) {
      console.error("Failed to save subtitle", error);
      toast({ title: "Save subtitle failed", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Headphones className="h-5 w-5" />
            Edit Audio
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="flex flex-1 flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="subtitles">Subtitles</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 overflow-y-auto px-1">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={formData.description}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>JLPT Level</Label>
                <MultiSelect
                  options={JLPT_LEVELS.map((level) => ({ value: level, label: level }))}
                  selected={[formData.jlpt_level]}
                  onChange={(selected) => {
                    const level = (selected[0] as JlptLevel) || "N5";
                    setFormData((prev) => ({
                      ...prev,
                      jlpt_level: level,
                    }));
                  }}
                  placeholder="Select JLPT"
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <MultiSelect
                  options={categoryOptions}
                  selected={formData.categories}
                  onChange={(selected) => {
                    const categories = normalizeCategoryList(selected);
                    setFormData((prev) => ({
                      ...prev,
                      categories,
                      category: categories[0] || "story",
                    }));
                  }}
                  allowCreate
                  onCreateOption={createCategory}
                  createText="Create"
                  searchPlaceholder="Search or create"
                  placeholder="Select category"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">Published</p>
                  <p className="text-sm text-muted-foreground">Visible to users</p>
                </div>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_published: checked,
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">Premium</p>
                  <p className="text-sm text-muted-foreground">Member-only listening</p>
                </div>
                <Switch
                  checked={formData.is_premium}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_premium: checked,
                    }))
                  }
                />
              </div>
            </div>

            <div className="flex justify-end pb-2">
              <Button onClick={saveBasic} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="media" className="space-y-5 overflow-y-auto px-1">
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Thumbnail
              </Label>
              <div className="flex items-start gap-4">
                {formData.thumbnail_url ? (
                  <img
                    src={formData.thumbnail_url}
                    alt="thumbnail"
                    className="h-24 w-40 rounded-lg border object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-40 items-center justify-center rounded-lg border bg-muted">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}

                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                  >
                    {uploadingThumbnail ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Replace thumbnail
                  </Button>

                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleUploadThumbnail}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Headphones className="h-4 w-4" />
                Audio File
              </Label>

              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{formData.audio_url}</p>
                    <p className="text-xs text-muted-foreground">
                      Duration: {Math.floor(audio.duration / 60)}:{String(audio.duration % 60).padStart(2, "0")}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => audioInputRef.current?.click()}
                    disabled={uploadingAudio}
                  >
                    {uploadingAudio ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Re-upload
                  </Button>

                  <input
                    ref={audioInputRef}
                    type="file"
                    className="hidden"
                    accept="audio/*"
                    onChange={handleUploadAudio}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pb-2">
              <Button onClick={saveBasic} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="subtitles" className="flex flex-1 flex-col overflow-hidden px-1">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{subtitles.length} subtitles</p>
              <Button size="sm" onClick={addSubtitle}>
                <Plus className="mr-1 h-4 w-4" />
                Add line
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto rounded-lg border">
              {loadingSubtitles ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : subtitles.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No subtitles</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28">Time</TableHead>
                      <TableHead>Japanese</TableHead>
                      <TableHead>Chinese</TableHead>
                      <TableHead>Romaji</TableHead>
                      <TableHead className="w-24 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subtitles.map((subtitle) => {
                      const isEditing = editingSubtitle?.id === subtitle.id;

                      return (
                        <TableRow key={subtitle.id}>
                          <TableCell className="text-xs font-mono text-muted-foreground">
                            {isEditing ? (
                              <div className="space-y-1">
                                <Input
                                  className="h-7 text-xs"
                                  value={formatSecondsToClock(editingSubtitle.start_time)}
                                  onChange={(event) =>
                                    setEditingSubtitle((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            start_time: parseClockToSeconds(event.target.value),
                                          }
                                        : prev,
                                    )
                                  }
                                />
                                <Input
                                  className="h-7 text-xs"
                                  value={formatSecondsToClock(editingSubtitle.end_time)}
                                  onChange={(event) =>
                                    setEditingSubtitle((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            end_time: parseClockToSeconds(event.target.value),
                                          }
                                        : prev,
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <>
                                {formatSecondsToClock(subtitle.start_time)}
                                <br />
                                {formatSecondsToClock(subtitle.end_time)}
                              </>
                            )}
                          </TableCell>

                          <TableCell>
                            {isEditing ? (
                              <Textarea
                                rows={2}
                                className="text-sm"
                                value={editingSubtitle.japanese}
                                onChange={(event) =>
                                  setEditingSubtitle((prev) =>
                                    prev ? { ...prev, japanese: event.target.value } : prev,
                                  )
                                }
                              />
                            ) : (
                              <span className="text-sm">{subtitle.japanese}</span>
                            )}
                          </TableCell>

                          <TableCell>
                            {isEditing ? (
                              <Textarea
                                rows={2}
                                className="text-sm"
                                value={editingSubtitle.chinese}
                                onChange={(event) =>
                                  setEditingSubtitle((prev) =>
                                    prev ? { ...prev, chinese: event.target.value } : prev,
                                  )
                                }
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground">{subtitle.chinese}</span>
                            )}
                          </TableCell>

                          <TableCell>
                            {isEditing ? (
                              <Textarea
                                rows={2}
                                className="text-sm font-mono"
                                value={editingSubtitle.romaji || ""}
                                onChange={(event) =>
                                  setEditingSubtitle((prev) =>
                                    prev ? { ...prev, romaji: event.target.value } : prev,
                                  )
                                }
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground">{subtitle.romaji}</span>
                            )}
                          </TableCell>

                          <TableCell className="text-right">
                            {isEditing ? (
                              <div className="flex justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => saveSubtitle(editingSubtitle)}
                                >
                                  <Save className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => {
                                    if (subtitle.isNew) {
                                      setSubtitles((prev) => prev.filter((row) => row.id !== subtitle.id));
                                    }
                                    setEditingSubtitle(null);
                                  }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setEditingSubtitle(subtitle)}
                                >
                                  <Edit2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => removeSubtitle(subtitle.id)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
